import { ProjectBulkAddResult } from 'api/graphQL/types/projectBulk.types'
import { BulkAddBarType } from 'components/WorkspaceEditor/types/bulkAddBarStore.type'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

/**
 * Creates a zustand store for managing bulk add bar  state
 */
export const useBulkAddBarStore = create<BulkAddBarType>()((set) => ({
    addBulkItemsInProgress: false,
    cancelBulkItems: false,
    addToProjectSuccess: false,
    bulkAddResponse: undefined,
    bulkAddErrorMessage: undefined,
    bulkAddBaseElementIds: [],
    actions: {
        setAddBulkItemsInProgress: (value: boolean) => {
            set({ addBulkItemsInProgress: value })
        },
        setCancelBulkItems: (value: boolean) => {
            set({ cancelBulkItems: value })
        },
        setAddToProjectSuccess: (value: boolean) => {
            set({ addToProjectSuccess: value })
        },
        setBulkAddResponse: (
            value: Partial<ProjectBulkAddResult> | undefined
        ) => {
            set({ bulkAddResponse: value })
        },
        setBulkAddErrorMessage: (value?: string) => {
            set({ bulkAddErrorMessage: value })
        },
        setBulkAddBaseElementIds: (ids: string[]) => {
            set({ bulkAddBaseElementIds: ids })
        },
    },
}))

/** Zustand selector for getting the actions/setters. */
export const useBulkAddBarAction = () =>
    useBulkAddBarStore((state) => state.actions)

/**
 * A custom hook that retrieves a shallowly selected value from the `useBulkAddBarStore` store.
 *
 * Sample usage:
 * ```typescript
 * const { bulkAddBaseElementIds, setBulkAddBaseElementIds } = useBulkAddBarShallowSelector((state) => ({
 *   bulkAddBaseElementIds: state.bulkAddBaseElementIds,
 *   setBulkAddBaseElementIds: state.actions.setBulkAddBaseElementIds,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useBulkAddBarShallowSelector = <T>(
    selector: (state: BulkAddBarType) => T
): T => useBulkAddBarStore(useShallow(selector))
